import React from 'react';
import { Modal, Space, Select, Typography, Alert, Input } from 'antd';
import type { ToPlnCostCenters } from '../../types';
import { useGetToPlnCostCenters } from '../../hooks/genericHooks';

const { Title } = Typography;

export interface ToCostCenterSelectProps {
  open: boolean;
  title?: string;
  // Generic modal API (used by GenericFormFill)
  onCancel?: () => void;
  onOk?: (value: string) => void; // resourceidname
  value?: any;
  options?: any[];
  // Specific API (used directly in pages)
  onClose?: () => void;
  onSelect?: (value: string) => void; // resourceidname
}

const ToCostCenterSelect: React.FC<ToCostCenterSelectProps> = ({ open, title = 'Select Cost Center', onClose, onSelect, onCancel, onOk }) => {
  const { data, isLoading, error } = useGetToPlnCostCenters();

  const source: ToPlnCostCenters[] = React.useMemo(() => data?.data ?? [], [data]);

  // Top-level queries that cascade into the combobox options
  const [filterQuery, setFilterQuery] = React.useState(''); // resource name filter
  const [categoryFilter, setCategoryFilter] = React.useState('');

  // Build unique option list for combobox (label/value = resourceidname), cascaded by both filters
  const options = React.useMemo(() => {
    const seen = new Set<string>();
    const q = filterQuery.toLowerCase();
    const cq = categoryFilter.toLowerCase();
    return source.reduce((acc: { label: string; value: string }[], r) => {
      if (!r.resourceidname) return acc;
      if (q && !r.resourceidname.toLowerCase().includes(q)) return acc;
      if (cq && !String(r.category || '').toLowerCase().includes(cq)) return acc;
      if (seen.has(r.resourceidname)) return acc;
      seen.add(r.resourceidname);
      acc.push({ label: r.resourceidname, value: r.resourceidname });
      return acc;
    }, []);
  }, [source, filterQuery, categoryFilter]);

  // Unique category options for the secondary combobox filter
  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>();
    source.forEach(r => { if (r.category) set.add(String(r.category)); });
    return Array.from(set).sort().map(v => ({ label: v, value: v }));
  }, [source]);
  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(undefined);

  const handleClose = () => {
    // Prefer the generic onCancel when provided; otherwise use onClose
    if (onCancel) onCancel();
    else onClose?.();
  };

  const handleOk = () => {
    if (selectedValue) {
      onSelect?.(selectedValue);
      onOk?.(selectedValue);
    }
    handleClose();
  };

  return (
    <Modal
      open={open}
      title={<Title level={4} style={{ margin: 0 }}>{title}</Title>}
      onCancel={handleClose}
      onOk={handleOk}
      closable
      maskClosable
      keyboard
      width={540}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {error && (
          <Alert type="error" message="Failed to load cost centers" description={error.message} showIcon />
        )}
        <Space wrap style={{ width: '100%' }}>
          <Input
            allowClear
            placeholder="Type to filter Resource Name"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            style={{ minWidth: 240 }}
          />
          <Select
            allowClear
            showSearch
            placeholder="Filter by Category"
            value={categoryFilter || undefined}
            onChange={(val) => setCategoryFilter(val || '')}
            options={categoryOptions}
            style={{ minWidth: 220 }}
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
          />
        </Space>
        <Select
          showSearch
          allowClear
          placeholder="Type to search Resource Name, then select"
          options={options}
          value={selectedValue}
          onChange={(val) => { setSelectedValue(val); }}
          filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
          style={{ width: '100%' }}
          loading={isLoading}
        />
      </Space>
    </Modal>
  );
};

export default ToCostCenterSelect;
